import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Phone } from 'lucide-react'
import apiClient from '@/api/client'
import type { ActiveOnlineOrder, OrderItem } from '@/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PinEntryModal } from '@/components/server/PinEntryModal'
import { toastHelpers } from '@/lib/toast-helpers'
import { ACTIVE_ONLINE_ORDERS_QUERY_KEY } from './ActiveOrdersBoard'

// Kitchen statuses a line can be voided from — VOIDABLE_STATUSES on the counter.
const VOIDABLE = new Set(['sent', 'preparing', 'ready', 'served'])

// RemoveAcceptedItemDialog — F-FE-N193 part B. After Accept the order is in
// the kitchen, so taking an item off is a void: pick the line here, then the
// counter's own void dialog asks "was it made?", a reason (Out of stock first)
// and a manager PIN, and sends the kitchen its VOID chit. The backend drops a
// promo the smaller order no longer reaches and tells the customer.
export function RemoveAcceptedItemDialog({
  order,
  onClose,
  formatCurrency,
}: {
  order: ActiveOnlineOrder | null
  onClose: () => void
  formatCurrency: (n: number) => string
}) {
  const queryClient = useQueryClient()
  const [line, setLine] = useState<OrderItem | null>(null)
  const orderId = order?.order_id ?? null

  const { data: live, isLoading, refetch } = useQuery({
    queryKey: ['orders', orderId, 'remove-item'],
    queryFn: () => apiClient.getOrder(orderId!).then((r) => r.data),
    enabled: !!orderId,
    staleTime: 0,
  })
  const lines = (live?.items ?? []).filter((i) => VOIDABLE.has(i.status))

  const done = () => {
    setLine(null)
    queryClient.invalidateQueries({ queryKey: ACTIVE_ONLINE_ORDERS_QUERY_KEY })
    onClose()
  }

  return (
    <>
      <Dialog open={!!order && !line} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="top-[6dvh] translate-y-0">
          <DialogHeader>
            <DialogTitle>Remove an item</DialogTitle>
            <DialogDescription>
              #{order?.order_number} · {order?.customer_name || 'Guest'}
              {order?.customer_phone && (
                <>
                  {' · '}
                  <a href={`tel:${order.customer_phone}`} className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline">
                    <Phone className="h-3 w-3" />
                    {order.customer_phone}
                  </a>
                </>
              )}
              . The kitchen already has this order: call the customer first, then pick what to take off. A manager PIN is needed.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading the order…</p>
          ) : lines.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing on this order can be removed.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {lines.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-foreground">
                      <span className="font-medium tabular-nums">{i.quantity}×</span> {i.product?.name ?? 'Item'}
                    </p>
                    {(i.modifiers?.length ?? 0) > 0 && (
                      <p className="truncate text-xs text-muted-foreground">
                        {i.modifiers!.map((m) => m.option_name).join(', ')}
                      </p>
                    )}
                    <p className="text-xs tabular-nums text-muted-foreground">{formatCurrency(i.unit_price)} each</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-danger-surface hover:text-danger-fg"
                    onClick={() => setLine(i)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {order && orderId && line && (
        <PinEntryModal
          orderId={orderId}
          itemId={line.id}
          itemName={line.product?.name ?? 'Item'}
          quantity={line.quantity}
          unitPrice={line.unit_price}
          itemStatus={line.status}
          defaultReasonCode="out_of_stock"
          onVoided={(data) => {
            const dropped = (data as { promo_code_dropped?: string | null } | undefined)?.promo_code_dropped
            if (dropped) {
              toastHelpers.error(
                'Promo removed',
                `The order is now below the minimum for ${dropped}, so the discount came off. Tell the customer.`,
              )
            }
          }}
          onSuccess={done}
          onClose={() => {
            setLine(null)
            void refetch()
          }}
        />
      )}
    </>
  )
}
